import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { UsoPorTipoDTO } from '../../../../models/master/master-dashboard.model';

/**
 * Movimentacoes por tipo (VENDA / PEDIDO / FINANCEIRO) — tres barras INDEPENDENTES.
 *
 * Barras separadas dizem "compare"; empilhada ou rosca diriam "somam". `porTipo[].empresas` e
 * `porTipo[].usuarios` NAO somam para os totais do bloco, entao nenhum grafico de composicao
 * pode ser usado aqui.
 *
 * O total de movimentacoes chega por `@Input` — e o campo `uso.movimentacoesNoPeriodo` do
 * contrato, JAMAIS um `reduce` sobre a serie, nem mesmo no callback do Chart.js.
 */
@Component({
  selector: 'app-master-dashboard-uso-chart',
  templateUrl: './master-dashboard-uso-chart.component.html',
  styleUrls: ['./master-dashboard-uso-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MasterDashboardUsoChartComponent implements OnInit, OnChanges, OnDestroy {

  @Input() dados: UsoPorTipoDTO[] = [];
  /** `uso.movimentacoesNoPeriodo` — recebido pronto, NUNCA somado a partir de `dados`. */
  @Input() totalMovimentacoes = 0;
  @Input() loading = false;

  chartData: any;
  chartOptions: any;
  ariaLabel = '';

  texts = {
    titulo: '',
    total_movimentacoes: '',
    vazio: '',
  };

  private langChangeSub?: Subscription;

  constructor(
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.buildChart();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
      this.buildChart();
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dados'] || changes['totalMovimentacoes']) {
      this.buildChart();
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }

  get temGrafico(): boolean {
    return (this.dados?.length ?? 0) > 0;
  }

  /**
   * Rotulo do tipo. Se a chave nao existir, o ngx-translate devolve a propria chave — nesse
   * caso mostramos o `tipo` cru: um tipo novo vindo do backend aparece com o nome de la, em vez
   * de sumir da tela.
   */
  rotuloDoTipo(tipo: string): string {
    const chave = `master.planos.dashboard.uso.tipos.${(tipo || '').toLowerCase()}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? (tipo || '') : traduzido;
  }

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('master.planos.dashboard.uso.titulo'),
      total_movimentacoes: this.translate.instant(
        'master.planos.dashboard.uso.total_movimentacoes',
      ),
      vazio: this.translate.instant('master.planos.dashboard.recebimentos.sem_dados'),
    };
    this.ariaLabel = this.texts.titulo;
  }

  private buildChart(): void {
    const serie = this.dados ?? [];

    this.chartData = {
      labels: serie.map((item) => this.rotuloDoTipo(item.tipo)),
      datasets: [
        {
          label: this.texts.titulo,
          data: serie.map((item) => item.quantidade),
          backgroundColor: ['#165c7d', '#3b82f6', '#22c55e'],
          borderRadius: 4,
          maxBarThickness: 64,
        },
      ],
    };

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            // `usuarios` nao entra em lugar nenhum desta entrega.
            label: (ctx: any) => {
              const item = serie[ctx.dataIndex ?? 0];
              const quantidade = ctx.parsed?.y ?? 0;
              const empresas = this.translate.instant(
                'master.planos.dashboard.uso.tooltip_empresas',
                { empresas: item?.empresas ?? 0 },
              );
              return `${quantidade} · ${empresas}`;
            },
            // Total do CAMPO do contrato. Nunca `serie.reduce(...)`.
            footer: () => `${this.totalMovimentacoes} ${this.texts.total_movimentacoes}`,
          },
        },
      },
      scales: {
        x: { grid: { display: false } },
        y: { min: 0, ticks: { maxTicksLimit: 6, precision: 0 } },
      },
    };
  }
}
